import React, { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import { round } from 'lodash';
import { Trash2, TriangleAlert } from 'lucide-react';
import { toast } from 'react-toastify';

import { DataTable, textColumn } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { TotemCsvInputButton } from 'components/TotemCsvInputButton';

import { CsvImportInfoButton } from 'components/CsvImportInfoButton';
import { GetTransferableProductsQuery, useGetTransferableProductsQuery, TotadminPermission } from 'data/__generated__';
import { ProductWithStocksSelector } from 'pages/Stocks/components/ProductWithStocksSelector';
import { handleCSVImport } from './csvImport';
import { PermissionGate } from 'components/PermissionGate';

type Product = GetTransferableProductsQuery['transferableProducts'][number];

export const ProductsToRemoveTable = ({
    columnId,
    selectedProductsToRemove,
    setSelectedProductsToRemove,
    loadingCsv,
}: {
    columnId: string;
    selectedProductsToRemove: GetTransferableProductsQuery['transferableProducts'];
    setSelectedProductsToRemove: React.Dispatch<
        React.SetStateAction<GetTransferableProductsQuery['transferableProducts']>
    >;
    loadingCsv: boolean;
}) => {
    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
    } = useGetTransferableProductsQuery({
        variables: {
            originColumnId: columnId,
            destinationColumnId: columnId, // re-use the stock-transfer query.
        },
        fetchPolicy: 'no-cache',
    });

    const updateProductQuantity = ({ quantity, product }: { quantity: number; product: Product }) => {
        if (quantity > product.originStockAvailable) return;
        setSelectedProductsToRemove((prev) =>
            prev.map((p) => (p.productId === product.productId ? { ...p, quantity } : p)),
        );
    };

    const onProductDelete = (productId: string) => {
        setSelectedProductsToRemove((prev) => prev.filter((p) => p.productId !== productId));
    };

    const columns = useMemo<ColumnDef<Product, unknown>[]>(
        () => [
            textColumn<Product, Record<string, unknown>>({
                accessorKey: 'fullname',
                header: 'Produit',
                defaultVisible: true,
                cell: ({ row }) => <span className="font-medium">{row.original.fullname}</span>,
            }),
            textColumn<Product, Record<string, unknown>>({
                accessorKey: 'originStockAvailable',
                header: 'Stock avant',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.originStockAvailable}</Centered>,
            }),
            textColumn<Product, Record<string, unknown>>({
                accessorKey: 'conditionning',
                header: 'Colisage',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.conditionning}</Centered>,
            }),
            textColumn<Product, Record<string, unknown>>({
                accessorKey: 'packagesPerBatch',
                header: 'Palettisation',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.packagesPerBatch}</Centered>,
            }),
            textColumn<Product, Record<string, unknown>>({
                id: 'pallets',
                accessorFn: () => '',
                header: 'Palettes',
                defaultVisible: true,
                cell: ({ row }) => {
                    const p = row.original;
                    if (p.packagesPerBatch <= 1) return null;
                    const max = round(p.originStockAvailable / (p.conditionning * p.packagesPerBatch), 2) || 0;
                    const value = round(p.quantity / (p.conditionning * p.packagesPerBatch), 2) || 0;
                    return (
                        <CompactInput
                            type="number"
                            value={value}
                            min={0}
                            step="1"
                            max={max}
                            data-test={`pallets-input-${p.productId}`}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (/^\d*\.?\d{0,2}$/.test(v)) {
                                    updateProductQuantity({
                                        product: p,
                                        // Rounded: fractional pallets would produce a non-integer
                                        // quantity, rejected by the Int! delta of the mutation.
                                        quantity: Math.round(Number(v) * p.conditionning * p.packagesPerBatch),
                                    });
                                }
                            }}
                        />
                    );
                },
            }),
            textColumn<Product, Record<string, unknown>>({
                id: 'colis',
                accessorFn: () => '',
                header: 'Colis',
                defaultVisible: true,
                cell: ({ row }) => {
                    const p = row.original;
                    if (p.conditionning <= 1) return null;
                    const max = round(p.originStockAvailable / p.conditionning, 2) || 0;
                    const value = round(p.quantity / p.conditionning, 2) || 0;
                    return (
                        <CompactInput
                            type="number"
                            value={value}
                            min={0}
                            step="1"
                            max={max}
                            data-test={`colis-input-${p.productId}`}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (/^\d*\.?\d{0,2}$/.test(v)) {
                                    updateProductQuantity({ product: p, quantity: Number(v) * p.conditionning });
                                }
                            }}
                        />
                    );
                },
            }),
            textColumn<Product, Record<string, unknown>>({
                id: 'quantity',
                accessorFn: (row) => row.quantity,
                header: 'Quantité à retirer',
                defaultVisible: true,
                cell: ({ row }) => {
                    const p = row.original;
                    return (
                        <div className="flex items-center justify-center gap-1">
                            {p.quantity < 0 ? <TriangleAlert className="size-4 text-yellow-500" /> : null}
                            <CompactInput
                                type="number"
                                value={p.quantity}
                                max={p.originStockAvailable}
                                data-test={`quantity-input-${p.productId}`}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^-?(\d+)?$/.test(v)) {
                                        updateProductQuantity({ product: p, quantity: Number(v) });
                                    }
                                }}
                            />
                            {p.quantity < 0 ? <TriangleAlert className="size-4 text-yellow-500" /> : null}
                        </div>
                    );
                },
            }),
            textColumn<Product, Record<string, unknown>>({
                id: 'newTotal',
                accessorFn: (row) => row.originStockAvailable - row.quantity,
                header: 'Nouvelle quantité',
                defaultVisible: true,
                cell: ({ row }) => {
                    const p = row.original;
                    return (
                        <CompactInput
                            type="number"
                            value={p.originStockAvailable - p.quantity}
                            min={0}
                            data-test={`new-total-input-${p.productId}`}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (/^(\d+)?$/.test(v)) {
                                    updateProductQuantity({
                                        product: p,
                                        quantity: p.originStockAvailable - Number(v),
                                    });
                                }
                            }}
                        />
                    );
                },
            }),
            textColumn<Product, Record<string, unknown>>({
                id: 'actions',
                accessorFn: () => '',
                header: '',
                defaultVisible: true,
                cell: ({ row }) => (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onProductDelete(row.original.productId)}
                        aria-label="Supprimer"
                        data-test={`delete-product-${row.original.productId}`}
                    >
                        <Trash2 className="size-4 text-destructive" />
                    </Button>
                ),
            }),
        ],
        [],
    );

    if (productsLoading) {
        return (
            <div className="p-4">
                <Loader mode={LoaderModeType.Spin} />
            </div>
        );
    }

    if (productsError || !productsData) {
        toast.error('Une erreur est survenue lors de la récupération des produits transférables');
        return null;
    }

    const { transferableProducts } = productsData;

    const onProductSelect = (productId: string) => {
        const tp = transferableProducts.find((p) => p.productId === productId);
        if (tp && !selectedProductsToRemove.find((p) => p.productId === productId)) {
            setSelectedProductsToRemove([...selectedProductsToRemove, { ...tp, quantity: 0 }]);
        }
    };

    return (
        <div className="flex w-full flex-1 min-h-0 flex-col gap-3">
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <PermissionGate requiredPermissions={[TotadminPermission.MicrostoreColumnsRead]}>
                        <ProductWithStocksSelector
                            productOption={null}
                            handleProductSelect={onProductSelect}
                            columnId={columnId}
                        />
                    </PermissionGate>
                </div>
                <TotemCsvInputButton
                    onCsvDataUpload={(csvData) =>
                        handleCSVImport({ csvData, transferableProducts, setSelectedProductsToRemove })
                    }
                    disabled={loadingCsv}
                />
                <CsvImportInfoButton title="Format du fichier CSV">
                    <p>
                        Le fichier doit contenir une ligne d'en-tête avec la colonne <Code>productId</Code> et au moins
                        une des colonnes de quantité ci-dessous (par ordre de priorité) :
                    </p>
                    <ul className="ml-5 list-disc space-y-1">
                        <li>
                            <Code>quantity</Code> — nombre d'unités à retirer (entier).
                        </li>
                        <li>
                            <Code>pallets</Code> — nombre de palettes ; converti en unités via{' '}
                            <em>colisage × palettisation</em>.
                        </li>
                        <li>
                            <Code>colis</Code> — nombre de colis ; converti en unités via <em>colisage</em>.
                        </li>
                    </ul>
                    <p>
                        Si plusieurs colonnes sont remplies sur la même ligne, l'ordre de priorité est{' '}
                        <Code>quantity</Code> &gt; <Code>pallets</Code> &gt; <Code>colis</Code>. Les produits déjà
                        ajoutés voient leur quantité mise à jour ; les nouveaux sont ajoutés à la liste.
                    </p>
                    <p className="text-muted-foreground">
                        Exemple : <Code>productId,quantity</Code>
                        <br />
                        <Code>abc123,5</Code>
                    </p>
                </CsvImportInfoButton>
            </div>

            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <TriangleAlert className="size-4 text-yellow-500" /> indique une nouvelle quantité négative.
            </p>

            <div className="min-h-0 flex-1">
                <DataTable<Product>
                    columns={columns}
                    data={selectedProductsToRemove}
                    filterMode="client"
                    getRowId={(row) => row.productId}
                />
            </div>
        </div>
    );
};

const Centered = ({ children }: { children: React.ReactNode }) => <div className="text-center">{children}</div>;

const CompactInput = (props: React.ComponentProps<typeof Input>) => (
    <Input {...props} className="h-8 w-20 text-center" />
);

const Code = ({ children }: { children: React.ReactNode }) => (
    <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>
);
